import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from 'revamp/reusable-components/tooltip/tooltip'
import Heatbar from '../../USStocksDetail/Heatbar'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import { RUPEE_SYMBOL } from '@/root/constants'
import { IMetaInfo, Performance } from '@/components/Stocks/Widgets/types'
import { formatNumberToCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'
import InStockL2Heading from '@/components/Stocks/Pages/InStockDetailPageV2/InL2Heading'
import Text from '../NewText'
import { getLastUpdatedTime } from '@/revamp/pods/common/utils/get-last-update'

interface Props {
  tooltip?: string
  heading?: string
  meta_info?: IMetaInfo
  disableCurrencyUnit?: boolean
  subheading?: string
  day_low?: number
  day_high?: number
  last_updated?: string
  marketDataHeading?: string
  returnsHeading?: string
}

export default function NewPerformance(props: Performance & Props) {
  const {
    tooltip,
    returns,
    market_stats,
    heading,
    performance_ranges,
    meta_info,
    disableCurrencyUnit = false,
    subheading = '',
    day_low,
    day_high,
    last_updated,
    marketDataHeading = 'Market Stats',
    returnsHeading = 'Historical Returns',
  } = props
  if (!props.performance_ranges?.length) {
    return null
  }

  const returnData = returns?.map(item => {
    return {
      ...item,
      key: item.label,
      value: `${item.value} %`,
      color:
        item.value && Math.sign(parseFloat(item.value.toString())) === -1
          ? '#DF3C27'
          : '#33A34D',
    }
  })
  const prefixSymbol = disableCurrencyUnit
    ? ''
    : meta_info
    ? meta_info.currency_symbol
    : RUPEE_SYMBOL

  const marketArr = [
    ...(day_low || day_high
      ? [
          {
            key: "Day's Low - High",
            value: day_low
              ? `${prefixSymbol}${formatNumberToCommaSeparatedString(day_low)}${
                  day_high
                    ? ` - ${prefixSymbol}${formatNumberToCommaSeparatedString(
                        day_high,
                      )}`
                    : ''
                }`
              : '-',
            addSymbol: false,
          },
        ]
      : []),
    ...(props['52week_low'] || props['52week_high']
      ? [
          {
            key: '52 Week Low - High',
            value: props['52week_low']
              ? `${prefixSymbol}${formatNumberToCommaSeparatedString(
                  props['52week_low'],
                )}${
                  props['52week_high']
                    ? ` - ${prefixSymbol}${formatNumberToCommaSeparatedString(
                        props['52week_high'],
                      )}`
                    : ''
                }`
              : '-',
          },
        ]
      : []),
  ]

  const marketsData = [
    ...(market_stats
      ? market_stats.map(item => {
          return {
            ...item,
            key: item.label,
            value: item.value,
          }
        })
      : []),
    ...marketArr,
  ]
  const isPositiveNumber = (inputString: string) => {
    const numericString = inputString.replace(/[^0-9.-]/g, '')
    const number = parseFloat(numericString)
    return number > 0
  }

  const getSubType = (subtype: string) => {
    return subtype.split('-').join(' ')
  }

  return (
    <section>
      <div className="flex items-center justify-between">
        <InStockL2Heading
          heading={heading}
          subheading={
            last_updated
              ? `as on ${getLastUpdatedTime(last_updated)} IST`
              : subheading
          }
        />

        {tooltip && (
          <Tooltip>
            <TooltipTrigger>
              <StencilCDNImage
                src="https://indcdn.indmoney.com/public/images/info_new.png"
                width={18}
                height={18}
                onClick={() => {}}
              />
            </TooltipTrigger>

            <TooltipContent>
              <div
                className="bg-brand-white border-1 shadow-one w-56 p-2 text-xs"
                dangerouslySetInnerHTML={{
                  __html: tooltip,
                }}
              />
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <ul className="mt-5 grid gap-8 md:grid-cols-2 md:gap-16">
        {performance_ranges?.map((el, index) => {
          return (
            <li key={index}>
              <div className="mb-1 flex flex-row justify-between">
                {el.down && (
                  <Text
                    appearance="caption"
                    addClassName="text-brand-grey-base"
                  >{`${getSubType(el.sub_type ?? '')}'s Low`}</Text>
                )}
                {/* <p className="text-brand-grey-base flex flex-col gap-1 px-2 text-center text-xs lg:flex-row lg:gap-2 lg:text-sm">
                    {el.voltl_text && <span>{el.voltl_text}:</span>}
                    {el.voltl && <span>{`${el.voltl.toFixed(2)}%`}</span>}
                  </p> */}
                {el.up && (
                  <Text
                    appearance="caption"
                    addClassName="text-brand-grey-base"
                  >{`${getSubType(el.sub_type ?? '')}'s High`}</Text>
                )}
              </div>
              <Heatbar
                title={el.label}
                leftPercentage={el.arrow_position}
                rightValue={
                  el.max
                    ? `${prefixSymbol}${formatNumberToCommaSeparatedString(
                        el.max,
                      )}`
                    : ''
                }
                leftValue={
                  el.min
                    ? `${prefixSymbol}${formatNumberToCommaSeparatedString(
                        el.min,
                      )}`
                    : ''
                }
                arrowValue={
                  el.val
                    ? `${prefixSymbol}${formatNumberToCommaSeparatedString(
                        el.val,
                      )}`
                    : ''
                }
              />
              <div className="flex items-center justify-between lg:mt-2">
                {el.down && (
                  <div className="flex items-center gap-2">
                    <StencilCDNImage
                      alt="downward going graph"
                      src="https://indcdn.indmoney.com/cdn/images/fe/downward-going-graph-icon.png"
                      width={20}
                      height={20}
                    />
                    <div className="flex flex-row flex-wrap gap-1 lg:gap-2">
                      <Text appearance="subtitle1">
                        {`${el.down.toFixed(2)}%`}
                      </Text>
                      <Text
                        appearance="caption"
                        addClassName="text-brand-grey-base"
                      >
                        Downside
                      </Text>
                    </div>
                  </div>
                )}

                {el.up && (
                  <div className="flex items-center gap-2">
                    <div className="flex flex-row flex-wrap gap-1 lg:gap-2">
                      <Text appearance="subtitle1">
                        {`${el.up.toFixed(2)}%`}
                      </Text>
                      <Text
                        appearance="caption"
                        addClassName="text-brand-grey-base"
                      >
                        Upside
                      </Text>
                    </div>

                    <StencilCDNImage
                      alt="downward going graph"
                      src="https://indcdn.indmoney.com/cdn/images/fe/upward-going-graph-icon.png"
                      width={20}
                      height={20}
                    />
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
      <div className="mt-10 grid grid-cols-1 justify-center gap-y-8 md:mt-20 md:grid-cols-2 md:gap-x-16">
        {/* MARKETS TABLE */}
        {!!marketsData?.length && (
          <div>
            <Text appearance="heading3" tag="h3" addClassName="mb-2.5">
              {marketDataHeading}
            </Text>
            <dl>
              {marketsData?.map(({ key, value }, index) => (
                <div
                  className="border-grey-tertiary flex items-center justify-between border-b py-2.5"
                  key={index}
                >
                  <dt className="text-grey-dark text-left text-sm font-normal">
                    {key}
                  </dt>
                  <dd className="text-right text-sm font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* RETURNS TABLE */}
        {!!returnData?.length && (
          <div>
            <Text appearance="heading3" tag="h3" addClassName="mb-2.5">
              {returnsHeading}
            </Text>
            <dl>
              {returnData?.map(({ key, value, color }, index) => (
                <div
                  className="border-grey-tertiary flex items-center justify-between border-b py-2.5"
                  key={index}
                >
                  <dt className="text-grey-dark text-left text-sm font-normal">
                    {key}
                  </dt>
                  <dd
                    className="text-right text-sm font-medium"
                    style={{ color }}
                  >
                    {isPositiveNumber(value) ? `+ ${value}` : value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </section>
  )
}
